import { create } from "zustand";
import { devtools } from "zustand/middleware";

export enum ConnectionStatus {
  CONNECTED = "connected",
  DISCONNECTED = "disconnected",
}

interface ConnectionState {
  status: ConnectionStatus;
  showDisconnectedOverlay: boolean;
  wasEverDisconnected: boolean;
}

interface ConnectionActions {
  markDisconnected: () => void;
  markConnected: () => void;
  setShowDisconnectedOverlay: (show: boolean) => void;
}

type ConnectionStore = ConnectionState & ConnectionActions;

const DEBUG = import.meta.env.VITE_DEBUG === "true";

// VoDemo's unload stop_lesson POST makes the backend close the tutor WS while
// the old page is still on screen. That close is ours, not the network's, so
// don't report it. VoDemo calls markPageUnloading from the pagehide listener
// it already has, not beforeunload: the lesson leave guard can cancel
// beforeunload with a "Leave site?" prompt, and a module-level beforeunload
// listener would cost every route its Firefox bfcache. pageshow clears it on a
// bfcache restore, and VoDemo clears it on unmount so it cannot outlive the
// page that set it.
let isPageUnloading = false;
export const markPageUnloading = () => {
  isPageUnloading = true;
};
export const clearPageUnloading = () => {
  isPageUnloading = false;
};
window.addEventListener("pageshow", clearPageUnloading);

const initialState: ConnectionState = {
  status: ConnectionStatus.CONNECTED,
  showDisconnectedOverlay: false,
  wasEverDisconnected: false,
};

const createConnectionStore = (set: any, get: any) => ({
  ...initialState,

  markDisconnected: () => {
    if (isPageUnloading) return;
    const state = get();
    if (state.status !== ConnectionStatus.DISCONNECTED) {
      set({
        status: ConnectionStatus.DISCONNECTED,
        wasEverDisconnected: true,
      });
    }
  },

  markConnected: () => {
    const state = get();
    if (state.status !== ConnectionStatus.CONNECTED) {
      set({
        status: ConnectionStatus.CONNECTED,
        showDisconnectedOverlay: false,
      });
    }
  },

  setShowDisconnectedOverlay: (show: boolean) => {
    // The overlay is only ever up while disconnected. The overlay timer runs
    // even when markDisconnected was suppressed, and markConnected only lowers
    // the overlay on a real status change, so without this a bfcache restore
    // mid-timer could leave it stuck over a connected page.
    if (
      show &&
      (isPageUnloading || get().status !== ConnectionStatus.DISCONNECTED)
    ) {
      return;
    }
    const current = get().showDisconnectedOverlay;
    if (current !== show) {
      set({ showDisconnectedOverlay: show });
    }
  },
});

export const useConnectionStore = create<ConnectionStore>(
  DEBUG
    ? devtools(createConnectionStore, { name: "ConnectionStore" })
    : createConnectionStore,
);
